import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession, sessionScope, getSession } from './auth';
import { apiGet, resolveApiUrl } from './api';
import { queryClient } from './queryClient';
import { useWebSocket, type WsMessage } from './ws';
import { activityRoom, notifyActivityOnce, notifyTaskAssignedOnce, trackActivityTab } from './activity-notifications';

export function useGlobalActivity(): void {
  const session = useSession(), scope = sessionScope(session), location = useLocation(), navigate = useNavigate();
  const tab = useRef(crypto.randomUUID());
  useEffect(() => {
    if (!session) return;
    const update = () => trackActivityTab(scope, tab.current, {roomId: activityRoom(location.pathname), visible: !document.hidden, at: Date.now()});
    update(); const timer = setInterval(update, 10_000); document.addEventListener('visibilitychange', update);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', update); trackActivityTab(scope, tab.current, null); };
  }, [scope, location.pathname]);
  const reconcile = useCallback(() => {
    if (sessionScope(getSession()) !== scope) return;
    for (const key of ['rooms', 'inbox', 'pins', 'task-summary', 'workspace', 'workspaces']) void queryClient.invalidateQueries({queryKey: [key]});
  }, [scope]);
  const onMessage = useCallback((event: WsMessage) => {
    if (sessionScope(getSession()) !== scope) return;
    if (event.type === 'activity.reset') {
      // Immediately unmount any old room/call and purge data whose ACL may have changed.
      navigate('/', {replace: true}); void queryClient.resetQueries(); return;
    }
    if (event.type === 'hello' || event.type === 'resume.required') { reconcile(); return; }
    if (event.type === 'task.assigned') {
      const task = event.payload as {task_id?: string; notification_id?: string; actor_id?: string} | undefined;
      void queryClient.invalidateQueries({queryKey: ['task-summary']});
      if (task?.task_id && task.notification_id) void notifyTaskAssignedOnce(scope, task.task_id, task.notification_id, task.actor_id).catch(() => {});
      return;
    }
    if (event.type !== 'activity.updated') return;
    reconcile();
    const payload = event.payload as {room_id?: string; kind?: string; message_id?: string; actor_id?: string} | undefined;
    if (!payload?.room_id) return;
    if (payload.kind?.startsWith('room.')) void queryClient.invalidateQueries({queryKey: ['room-members', payload.room_id]});
    if (payload.kind === 'message.created' && payload.message_id && payload.actor_id !== session?.user.id) {
      void notifyActivityOnce(scope, payload.room_id, payload.message_id).catch(() => {});
    }
  }, [scope, session?.user.id, reconcile, navigate]);
  const address = new URL(resolveApiUrl('/v1/me/events/ws'), window.location.origin);
  address.protocol = address.protocol === 'https:' ? 'wss:' : 'ws:';
  useWebSocket(address.toString(), {token: session?.token, enabled: !!session, onMessage, onOpen: reconcile,
    onDenied: () => { if (sessionScope(getSession()) === scope) void apiGet('/v1/me/profile').then(reconcile).catch(() => {}); },
  });
}
